import { useEffect, useState } from "react";
import type { ChangeHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import Button from "@/design-system/button";
import Checkbox from "@/design-system/checkbox";
import Modal from "@/design-system/modal/modal";
import ReadMoreText from "@/design-system/read-more-text";
import TelemetryButton from "@/design-system/telemetry-button";
import { getAssetsImage } from "@/lib/cdn-helper";
import { sendTelemetryEvent } from "@/lib/telemetry";
import type { ConsentScreenType, IConsentItem } from "@/src/modals/useConsents";
import { sendToGTMEventClick } from "@Components/Modules/Helpers";
import { useDeviceFingerprint } from "@Components/ZeroFraud";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, colors, genericStyles } from "../../../styles/tokens.stylex";

export type ISocialLogin = {
    isRegistration: boolean;
    consents: IConsentItem[];
    consentErrorMessage: string;
    showMoreText: string;
    showLessText: string;
    modalConfirmationButton: string;
    onConsentStatusChange: ChangeHandler;
    consentScreenType: ConsentScreenType;
    channel?: "Plugin";
    formSubmitText: string;
};

type FormFields = { consents: { [id: string]: boolean } };

export default function SocialLogin({
    isRegistration,
    formSubmitText,
    consents,
    consentErrorMessage,
    showMoreText,
    showLessText,
    modalConfirmationButton,
    onConsentStatusChange,
    consentScreenType,
    channel,
}: ISocialLogin) {
    const [activeProvider, setActiveProvider] = useState<{
        ref: HTMLButtonElement | HTMLAnchorElement;
        provider: string;
    }>();

    const [showConsentModal, setShowConsentModal] = useState(false);
    const [useDefaultPost, setUseDefaultPost] = useState(false);
    const fingerprint = useDeviceFingerprint();

    const { handleSubmit, register, formState, setValue } = useForm<FormFields>();

    useEffect(() => {
        consents?.forEach((value) => {
            setValue(`consents.${value.id}`, !!value.isSelectedByDefault);
        });
    }, [consents, setValue]);

    const onSubmit = (fields: FormFields) => {
        const requiredConsents = consents?.filter(
            ({ isRequired, isSelectable, id }) => isSelectable && isRequired && !fields.consents[id],
        );
        if (requiredConsents?.length) {
            setShowConsentModal(true);
            return;
        }

        const url = new URL(window.location.href);
        url.hash = "";
        window.history.replaceState(null, "", url);

        setUseDefaultPost(true);
    };

    useEffect(() => {
        if (useDefaultPost) {
            requestAnimationFrame(() => activeProvider?.ref?.click());
        }
    }, [activeProvider?.ref, useDefaultPost]);

    const trackEvents = (ev: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
        if (!useDefaultPost) {
            return;
        }

        const authenticationProvider = (ev.currentTarget as HTMLButtonElement).value;
        sendToGTMEventClick(`rgstr_${authenticationProvider.toLocaleLowerCase()}`);
        sendTelemetryEvent({
            actionType: "Click",
            eventName: `${isRegistration ? "rgstr" : "login"}-${authenticationProvider}`,
            channel,
        });
    };

    const buttonFactory = (provider: string, content: string) => {
        const title = formSubmitText.replace("{ProviderName}", provider);
        return (
            <Button
                variant="none"
                name="provider"
                as="button"
                size="None"
                type="submit"
                value={provider}
                title={title}
                styleX={styles.socialBtn}
                onClick={(ev) => {
                    setActiveProvider({ ref: ev.currentTarget, provider });
                    trackEvents(ev);
                }}
            >
                <img {...stylex.props(styles.socialBtnImg)} src={content} alt={title} />
            </Button>
        );
    };

    const ExternalLogin = "/account/ExternalLogin";
    const ExternalRegister = "/account/ExternalRegister";

    const actionUrl = `${isRegistration ? ExternalRegister : ExternalLogin}`;

    useEffect(() => {
        if (showConsentModal) {
            sendTelemetryEvent({
                actionType: "View",
                eventName: "rgstr-TermsConditionsCheckbox",
            });
        }
    }, [showConsentModal]);

    return (
        <>
            <form
                onSubmit={useDefaultPost ? undefined : handleSubmit(onSubmit)}
                action={actionUrl}
                {...stylex.props(styles.socialLoginComponent)}
                method="Post"
            >
                {buttonFactory("Google", getAssetsImage("logos/Google.svg"))}
                {buttonFactory("Facebook", getAssetsImage("logos/Facebook.svg"))}
                {buttonFactory("Apple", getAssetsImage("logos/Apple.svg"))}
                {buttonFactory("Onet", getAssetsImage("logos/Okonto.svg"))}
                {consents?.map((value, index) => (
                    <input key={value.id} type="hidden" name={`consents[${index}].id`} value={value.id} />
                ))}
                {Object.keys(formState?.touchedFields?.consents || {}).map((value, index) => {
                    return (
                        <input
                            key={value || ""}
                            type="hidden"
                            name={`consents[${index}].isChecked`}
                            value={formState?.touchedFields?.consents?.[value] ? "true" : "false"}
                        />
                    );
                })}
                <input type="hidden" name="fgp" value={fingerprint} />
                <input type="hidden" name="return" value={window.location.href} />
                <input type="hidden" name="consentScreenType" value={consentScreenType} />
            </form>
            {showConsentModal && (
                <Modal onClose={() => setShowConsentModal(false)}>
                    <form
                        {...stylex.props(styles.modalForm)}
                        onSubmit={handleSubmit(onSubmit)}
                        action={actionUrl}
                        method="Post"
                    >
                        {consents
                            ?.filter((value) => value.isSelectable)
                            .map((value) => (
                                <Checkbox
                                    key={value.id}
                                    id={value.id}
                                    checked={value.isSelectedByDefault}
                                    errorMessage={formState.errors.consents?.[value.id]?.message}
                                    {...register(`consents.${value.id}`, {
                                        required: value.isRequired && consentErrorMessage,
                                        onChange: onConsentStatusChange,
                                    })}
                                >
                                    <ReadMoreText
                                        longDescription={value.longDescription}
                                        shortDescription={value.shortDescription}
                                        startShort={true}
                                        shortButtonText={showMoreText}
                                        longButtonText={showLessText}
                                    />
                                </Checkbox>
                            ))}
                        <div>
                            <TelemetryButton
                                as="button"
                                variant="magentaRound"
                                type="submit"
                                size="M"
                                eventName="common-next"
                            >
                                {modalConfirmationButton}
                            </TelemetryButton>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
}

const styles = stylex.create({
    socialLoginComponent: {
        gap: ComposableSpacing.s3,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
    },

    socialBtn: {
        borderColor: colors.b_Primary,
        borderRadius: genericStyles.borderRadiusFullRound,
        borderStyle: "solid",
        borderWidth: "1px",
        boxShadow: {
            default: null,
            ":hover": `0 0 .3em ${colors.b_Primary}`,
        },
        display: "inline-flex",
        lineHeight: "1.25em",
        paddingBottom: "0.625em",
        paddingLeft: "0.625em",
        paddingRight: "0.625em",
        paddingTop: "0.625em",
    },

    socialBtnImg: {
        objectFit: "contain",
        height: "1.45em",
        width: "1.45em",
    },

    modalForm: {
        gap: "1em",
        display: "flex",
        flexDirection: "column",
        marginTop: ComposableSpacing.s8,
    },
});
